import { useState } from 'react'
import { IoIosArrowDown, IoIosArrowUp, IoIosCloseCircleOutline } from 'react-icons/io'
import Papa from "papaparse"
import { useHistory } from "react-router-dom"
import { BASE_URL, fetchAll } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import useSWR from 'swr'
import axios from 'axios'

const UploadMillerModal = ({
  onClose,
  mutate
}) => {
  const [showInstruction, setShowInstruction] = useState(false)
  const [data, setData] = useState([])
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [validationErrors, setValidationErrors] = useState(null)
  const [showErrorView, setShowErrorView] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const history = useHistory()
  const { data: groupsData, isLoading: loadingGroups } = useSWR(`groups/`, fetchAll)

  const handleCopyAll = () => {
    if (!validationErrors?.validation_errors) return

    const summary = `Upload Errors Summary:\nTotal Processed: ${validationErrors.total_rows_processed}, Valid: ${validationErrors.valid_rows}, Invalid: ${validationErrors.invalid_rows}\n\n`
    const details = validationErrors.validation_errors.map(err => {
      const farmerInfo = err.data ? ` (${err.data.firstname} ${err.data.lastname})` : ""
      return `- Row ${err.row}${farmerInfo}: ${err.errors.join(", ")}`
    }).join("\n")

    const fullText = `${summary}Details:\n${details}`

    navigator.clipboard.writeText(fullText).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    })
  }

  // Download template
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(
        `${BASE_URL}farmer-bulk-upload/download_template/`,
        {
          headers: {
            "Authorization": `Bearer ${token}`
          },
          responseType: 'blob'
        }
      )

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'farmers_template.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      ErrorNotification("Failed to download template. Please try again.")
    }
  }

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]

    if (selectedFile && !selectedFile.name.endsWith('.csv')) {
      ErrorNotification("Invalid file type. Only .csv files are supported.")
      event.target.value = null
      setFile(null)
      setData([])
      return
    }

    setFile(selectedFile)

    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      Papa.parse(selectedFile, {
        encoding: "utf-8",
        header: true,
        complete: (result) => {
          setData(result.data)
        },
        error: (error) => {
          console.error(error)
        },
      })
    } else {
      setData([]) // Clear data preview for Excel
    }
  }

  // Handle file upload
  const handleSubmit = async () => {
    if (!file) {
      ErrorNotification("Please select a file before uploading.")
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    if (selectedGroup) {
      formData.append('farmer_group_ids', selectedGroup)
    }

    try {
      const response = await axios.post(
        `${BASE_URL}farmer-bulk-upload/upload_farmers/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
        }
      )
      mutate()
      setLoading(false)

      SuccessNotification(response?.data?.message || "You have successfully uploaded farmers")
      onClose()
    } catch (error) {
      setLoading(false)
      // Check for structured validation errors (bulk upload failure)
      if (error.response?.data?.validation_errors) {
        setValidationErrors(error.response.data)
        setShowErrorView(true)
        return
      }

      // Check if the backend actually processed the file with warnings
      if (error.response?.status === 400 && error.response.data?.detail?.includes("added")) {
        SuccessNotification("Farmers were uploaded but with warnings.")
        mutate()
        onClose()
      } else {
        ErrorNotification(error.response?.data?.error || error.response?.data?.message || "Upload failed")
      }
    }
  }


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white overflow-hidden dark:bg-dark rounded-lg shadow-lg w-full md:max-w-[1000px] max-w-[90vw] relative max-h-[90vh] flex flex-col">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            {showErrorView ? 'Upload Validation Errors' : 'Add A New Farmer'}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        {showErrorView ? (
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark">
            <div className="p-6 space-y-6">
              {/* Summary Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-secondaryDark p-4 rounded-xl border dark:border-border-dark shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Processed</p>
                  <p className="text-2xl font-bold dark:text-white">{validationErrors?.total_rows_processed || 0}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-xl border border-green-100 dark:border-green-800 shadow-sm">
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">Valid Rows</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-300">{validationErrors?.valid_rows || 0}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-xl border border-red-100 dark:border-red-800 shadow-sm">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">Invalid Rows</p>
                  <p className="text-2xl font-bold text-red-700 dark:text-red-300">{validationErrors?.invalid_rows || 0}</p>
                </div>
              </div>

              {/* Error Message */}
              <div className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="font-semibold text-sm">
                  {validationErrors?.message || "Please fix validation errors before uploading"}
                </div>
              </div>

              {/* Detailed Errors Table */}
              <div className="bg-white dark:bg-secondaryDark border dark:border-border-dark rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b dark:border-border-dark bg-gray-100 dark:bg-secondaryDark flex justify-between items-center text-sm md:text-base">
                  <h3 className="font-bold text-gray-700 dark:text-white">Validation Errors (Row by Row)</h3>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={handleCopyAll}
                      className="flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-dark border dark:border-border-dark rounded-md text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-secondaryDark transition-all active:scale-95"
                    >
                      {isCopied ? (
                        <>
                          <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-green-600 dark:text-green-400">Copied!</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                          <span>Copy All</span>
                        </>
                      )}
                    </button>
                    <span className="hidden sm:inline-block text-xs bg-red-500 text-white px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                      Required Fixes
                    </span>
                  </div>
                </div>
                <div className="divide-y dark:divide-border-dark max-h-[400px] overflow-y-auto">
                  {validationErrors?.validation_errors?.map((err, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-dark/40 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="bg-red-500 text-white text-xs font-bold w-12 h-6 flex items-center justify-center rounded-md flex-shrink-0">
                          Row {err.row}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {err.errors.map((msg, mIdx) => (
                              <span key={mIdx} className="text-sm text-red-600 dark:text-red-400 font-medium">
                                • {msg}
                              </span>
                            ))}
                          </div>
                          
                          {/* Farmer Context */}
                          {err.data && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 italic bg-gray-100 dark:bg-dark/20 p-2 rounded border-l-4 border-yellowcolor">
                              Context: Farmer <span className="font-bold text-gray-700 dark:text-gray-300">{err.data?.firstname} {err.data?.lastname}</span>
                              {err.data?.email && ` (${err.data?.email})`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="p-5 space-y-4">
            <h2 className="text-base font-medium text-yellowcolor">
              Upload farmer details
            </h2>

            <div className='bg-[#C6DBFD] p-4 rounded-lg '>
              <button className='flex transition-all duration-300 flex-row items-center justify-between w-full' onClick={() => setShowInstruction(!showInstruction)}>
                <h2 className='text-sm font-medium text-blue-600'>
                  Upload a file
                </h2>
                {showInstruction ? <IoIosArrowUp className='h-5 w-5 text-blue-600' /> : <IoIosArrowDown className='h-5 w-5 text-blue-600' />}
              </button>

              {showInstruction && (
                <div className='transition-all text-blue-900 text-sm duration-300 mt-4 space-y-4'>
                  <p>
                        1. Upload an excel spreadsheet  <span className="font-black">(.csv) </span> containing data in the template file that was downloaded
                    <p >Only columns that are specified in the provided templates will be uploaded. The supported columns are
                      <p className="font-semibold"> firstname,lastname,email,contact,district,region,community,gender, <br />dob,education,farm_name,farm_region,acres,farm_district,farm_community,farm_longitude,farm_latitude</p>
                    </p>
                  </p>
                  <p >2. To upload a file, click on the choose file option below to select the filled template file and then click on the upload button to upload the data</p>
                  <p >3. If there are any errors, the system will prompt you and the upload will be rejected</p>
                </div>
              )}
            </div>
          </div>

          <div className='p-4 flex flex-col-reverse md:flex-row gap-4 w-full'>
            <div className='w-full'>

              <div className='gap-2 w-full mb-6'>
                <label htmlFor="ghana_card_number" className="block text-base font-medium text-gray-700 dark:text-white mb-1">
                  Group (Optional)
                </label>
                <select
                  name="group"
                  id="group"
                  className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e.target.value)
                  }}
                >
                  <option value="">Select Group</option>
                  {Array.isArray(groupsData) && groupsData.map((grp) => <option className="capitalize" key={grp?.id} value={grp?.id}>
                    {grp?.group_name}
                  </option>)}
                </select>
              </div>


              <label htmlFor="file" className="w-full cursor-pointer">
                <div className='flex flex-col items-center justify-center border border-dashed border-gray-200 rounded-md p-4 h-96'>
                  {file ? <>
                    <svg width="65" height="65" viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4.17188" y="3.95898" width="56.6461" height="56.6461" rx="28.323" fill="#FEECB8" />
                      <rect x="4.17188" y="3.95898" width="56.6461" height="56.6461" rx="28.323" stroke="#FFF9E8" stroke-width="7.08076" />
                      <path d="M34.852 20.7985V25.673C34.852 26.334 34.852 26.6644 34.9807 26.9169C35.0938 27.1389 35.2743 27.3195 35.4964 27.4326C35.7488 27.5612 36.0793 27.5612 36.7402 27.5612H41.6148M41.9328 29.9076V38.4184C41.9328 40.4012 41.9328 41.3926 41.5469 42.1499C41.2075 42.8161 40.6659 43.3577 39.9997 43.6971C39.2424 44.083 38.251 44.083 36.2682 44.083H28.7154C26.7326 44.083 25.7412 44.083 24.9839 43.6971C24.3177 43.3577 23.7761 42.8161 23.4367 42.1499C23.0508 41.3926 23.0508 40.4012 23.0508 38.4184V26.1451C23.0508 24.1623 23.0508 23.1709 23.4367 22.4135C23.7761 21.7474 24.3177 21.2058 24.9839 20.8663C25.7412 20.4805 26.7326 20.4805 28.7154 20.4805H32.5057C33.3716 20.4805 33.8046 20.4805 34.2121 20.5783C34.5733 20.665 34.9186 20.8081 35.2354 21.0022C35.5927 21.2211 35.8989 21.5273 36.5112 22.1396L40.2737 25.9021C40.886 26.5144 41.1921 26.8206 41.4111 27.1779C41.6052 27.4946 41.7483 27.84 41.835 28.2012C41.9328 28.6087 41.9328 29.0416 41.9328 29.9076Z" stroke="#E5B117" stroke-width="2.36025" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>


                    {/* file name */}
                    <p className='text-base text-black dark:text-white'>
                      {file?.name}
                    </p>

                    {/* file size */}
                    <p className='text-sm text-yellowcolor font-bold'>
                      {file?.size < 1024 * 1024 
                        ? `${(file?.size / 1024).toFixed(2)} KB` 
                        : `${(file?.size / (1024 * 1024)).toFixed(2)} MB`}
                    </p>
                  </> : <>
                    <svg width="41" height="43" viewBox="0 0 41 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.49414 9.5V29.5C1.49414 33.918 8.65714 37.5 17.4941 37.5C18.8751 37.5 20.2151 37.413 21.4941 37.248M1.49414 9.5C1.49414 13.918 8.65714 17.5 17.4941 17.5C26.3311 17.5 33.4941 13.918 33.4941 9.5M1.49414 9.5C1.49414 5.082 8.65714 1.5 17.4941 1.5C26.3311 1.5 33.4941 5.082 33.4941 9.5M33.4941 9.5V23.5M33.4941 19.5C33.4941 23.918 26.3311 27.5 17.4941 27.5C8.65714 27.5 1.49414 23.918 1.49414 19.5M33.4941 29.5V35.5M33.4941 35.5V41.5M33.4941 35.5H39.4941M33.4941 35.5H27.4941" stroke="#888888" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <p className='text-base font-light text-gray-500 mt-3'>
                      Kindly select a file by clicking the button above.
                    </p>
                    <p className='text-xs font-semibold text-blue-600 mt-1 uppercase'>
                            Supported format: .csv
                    </p>
                    <p className='text-sm text-black dark:text-white mt-2'>
                      No file selected
                    </p>
                  </>}
                </div>

                <input
                  type="file"
                  id="file"
                  className='hidden'
                  onChange={handleFileChange}
                      accept=".csv"
                />
              </label>

            </div>

            <div className='md:w-2/3 w-full bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center'>
              <svg width="33" height="43" viewBox="0 0 33 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M31.4941 26V20.75C31.4941 18.9598 30.783 17.2429 29.5171 15.977C28.2512 14.7112 26.5344 14 24.7441 14H21.7441C21.1474 14 20.5751 13.7629 20.1532 13.341C19.7312 12.919 19.4941 12.3467 19.4941 11.75V8.75C19.4941 6.95979 18.783 5.2429 17.5171 3.97703C16.2512 2.71116 14.5344 2 12.7441 2H8.99414M10.4941 26L16.4941 32M16.4941 32L22.4941 26M16.4941 32V20M13.4941 2H3.74414C2.50214 2 1.49414 3.008 1.49414 4.25V38.75C1.49414 39.992 2.50214 41 3.74414 41H29.2441C30.4861 41 31.4941 39.992 31.4941 38.75V20C31.4941 15.2261 29.5977 10.6477 26.2221 7.27208C22.8464 3.89642 18.268 2 13.4941 2Z" stroke="#275745" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <p className='text-base font-light text-green-700 mt-5'>
                Download a sample Excel file
              </p>
              <button onClick={handleDownload}
                className='text-sm font-light underline p-2 text-green-700'>
                Click Here to download
              </button>
            </div>
            </div>
          </div>
        )}

        <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
          <div className="flex flex-row items-end justify-end gap-4">
            {showErrorView ? (
              <button
                onClick={() => {
                  setShowErrorView(false)
                  setValidationErrors(null)
                }}
                className='px-6 text-sm py-2 bg-yellowcolor hover:bg-yellowcolor/90 text-black font-bold rounded-lg shadow-sm transition-all duration-200'>
                Back to Upload
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
                  Close
                </button>

                <button
                  disabled={!file || loading}
                  onClick={handleSubmit}
                  className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${!file || loading ? "opacity-50 cursor-not-allowed" : ""}`}>
                  {loading ? "Please wait..." : "Upload"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadMillerModal